import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { user } from '../../auth'
import { Book } from '../../content'
import { ReadingSession } from '../tracker'

// =============================================================================
// CONTINUE READING - FORMER lookup table for "Lanjut Baca". NOT READ OR WRITTEN ANY MORE.
// The carousel is computed live by `continueReadingLive`
// (packages/trigger/src/metrics/live/continue-reading.ts). The table remains until a
// later migration drops it; its rows are frozen — do not read them.
// =============================================================================

export const ContinueReading = pgTable(
  'continue_reading',
  {
    id: uuid().defaultRandom().primaryKey(),
    userId: text()
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    bookId: uuid()
      .references(() => Book.id, { onDelete: 'cascade' })
      .notNull(),
    currentPage: integer().notNull().default(0), // Last page number the user read (1-based; 0 = not started)
    readingSessionId: uuid().references(() => ReadingSession.id, { onDelete: 'set null' }), // Optional link to latest reading session for resume
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_continue_reading_user').on(t.userId),
    index('idx_continue_reading_book').on(t.bookId),
    index('idx_continue_reading_user_updated').on(t.userId, t.updatedAt), // For "latest N" queries
    unique('uq_continue_reading_user_book').on(t.userId, t.bookId),
  ],
)

export const continueReadingRelations = relations(ContinueReading, ({ one }) => ({
  user: one(user, {
    fields: [ContinueReading.userId],
    references: [user.id],
  }),
  book: one(Book, {
    fields: [ContinueReading.bookId],
    references: [Book.id],
  }),
  readingSession: one(ReadingSession, {
    fields: [ContinueReading.readingSessionId],
    references: [ReadingSession.id],
  }),
}))

export const InsertContinueReadingSchema = createInsertSchema(ContinueReading).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateContinueReadingSchema = createUpdateSchema(ContinueReading).partial().omit({
  id: true,
  userId: true,
  bookId: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectContinueReadingSchema = createSelectSchema(ContinueReading)

export type InsertContinueReading = typeof ContinueReading.$inferInsert
export type UpdateContinueReading = z.infer<typeof UpdateContinueReadingSchema>
export type SelectContinueReading = typeof ContinueReading.$inferSelect
